import { __assign } from "tslib";
import React, { useReducer, useEffect } from 'react';
import Button from '../../../../Button';
import { LayoutGridIcon, LayoutMasonryIcon } from '../icons';
import Radio from './_partials/RadioOption';
import { GalleryLayout, getDefaultComponentData, CropFit } from '../types';
import Input from '../../../../Input';
import debounce from 'lodash/debounce';
import s from './gallery-settings-modal.scss';
function formReducer(state, action) {
    switch (action.type) {
        case 'setLayout':
            return __assign(__assign({}, state), { galleryLayout: action.galleryLayout });
        case 'setCrop':
            return __assign(__assign({}, state), { cropping: action.cropping });
        case 'setGridItemWidth':
            return __assign(__assign({}, state), { gridItemWidth: action.width });
        case 'setGridItemHeight':
            return __assign(__assign({}, state), { gridItemHeight: action.height });
        case 'setMasonryRowHeight':
            return __assign(__assign({}, state), { masonryRowHeight: action.height });
        case 'setSpacing':
            return __assign(__assign({}, state), { spacing: action.spacing });
        default:
            throw new Error();
    }
}
var layoutRadioTheme = {
    className: s['layout-radio'],
    iconClassName: s['layout-icon'],
    iconCheckedClassName: s['layout-radio-checked'],
    radioClassName: s['layout-radio-input'],
};
var stdRadioTheme = {
    className: s['crop-radio'],
};
var defaultState = getDefaultComponentData();
var setStyles = function (store, key, value) {
    var _a;
    var data = store.get('componentData') || defaultState;
    return store.set('componentData', __assign(__assign({}, data), { styles: __assign(__assign({}, data.styles), (_a = {}, _a[key] = value, _a)) }));
};
var GallerySettingsModal = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var t = _a.t, componentData = _a.componentData, pubsub = _a.pubsub, helpers = _a.helpers;
    var initState = {
        galleryLayout: ((_b = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _b === void 0 ? void 0 : _b.galleryLayout) || defaultState.styles.galleryLayout,
        cropping: ((_c = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _c === void 0 ? void 0 : _c.cropping) || defaultState.styles.cropping,
        gridItemWidth: ((_d = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _d === void 0 ? void 0 : _d.gridItemWidth) || defaultState.styles.gridItemWidth,
        gridItemHeight: ((_e = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _e === void 0 ? void 0 : _e.gridItemHeight) ||
            defaultState.styles.gridItemHeight,
        masonryRowHeight: ((_f = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _f === void 0 ? void 0 : _f.masonryRowHeight) ||
            defaultState.styles.masonryRowHeight,
        spacing: ((_g = componentData === null || componentData === void 0 ? void 0 : componentData.styles) === null || _g === void 0 ? void 0 : _g.spacing) || defaultState.styles.spacing,
    };
    var _h = useReducer(formReducer, initState), state = _h[0], dispatch = _h[1];
    var setGalleryLayout = function (e) {
        var galleryLayout = parseInt(e.target.value);
        dispatch({ type: 'setLayout', galleryLayout: galleryLayout });
        setStyles(pubsub.store, 'galleryLayout', galleryLayout);
    };
    var setCropRules = function (e) {
        var cropping = e.target.value;
        dispatch({ type: 'setCrop', cropping: cropping });
        setStyles(pubsub.store, 'cropping', cropping);
    };
    var setGridItemWidth = function (e) {
        var width = parseInt(e.target ? e.target.value : e);
        dispatch({
            type: 'setGridItemWidth',
            width: width,
        });
        setStyles(pubsub.store, 'gridItemWidth', width);
    };
    var setGridItemHeight = function (e) {
        var height = parseInt(e.target ? e.target.value : e);
        dispatch({
            type: 'setGridItemHeight',
            height: height,
        });
        setStyles(pubsub.store, 'gridItemHeight', height);
    };
    var setMasonryRowHeight = function (e) {
        var height = parseInt(e.target ? e.target.value : e);
        dispatch({
            type: 'setMasonryRowHeight',
            height: height,
        });
        setStyles(pubsub.store, 'masonryRowHeight', height);
    };
    var setSpacing = function (e) {
        var spacing = parseInt(e.target ? e.target.value : e);
        dispatch({
            type: 'setSpacing',
            spacing: spacing,
        });
        setStyles(pubsub.store, 'spacing', spacing);
    };
    useEffect(function () {
        document.body.style.overflowY = 'scroll';
        return function () {
            document.body.style.overflowY = 'auto';
        };
    }, []);
    return (React.createElement("div", { className: s['container'] },
        React.createElement("h2", { className: s['title'] }, t('editor.gallery.settings.title')),
        React.createElement("label", { className: s['label'], id: "layout-radio-label" }, t('editor.gallery.settings.layout')),
        React.createElement("div", { className: s['radio-group'], role: "radiogroup", "aria-labelledby": "layout-radio-label" },
            React.createElement(Radio, __assign({ label: t('editor.gallery.settings.galleryLayout.grid'), icon: LayoutGridIcon, value: GalleryLayout.GRID, onChange: setGalleryLayout, checked: state.galleryLayout === GalleryLayout.GRID }, layoutRadioTheme)),
            React.createElement(Radio, __assign({ label: t('editor.gallery.settings.galleryLayout.masonry'), icon: LayoutMasonryIcon, value: GalleryLayout.MASONRY, onChange: setGalleryLayout, checked: state.galleryLayout === GalleryLayout.MASONRY }, layoutRadioTheme))),
        React.createElement("label", { className: s['label'], id: "cropping-radio-label" }, t('editor.gallery.settings.cropping')),
        React.createElement("div", { className: s['radio-group'], role: "radiogroup", "aria-labelledby": "cropping-radio-label" },
            React.createElement(Radio, __assign({ label: t('editor.gallery.settings.cropping.crop'), value: 'crop', onChange: setCropRules, checked: state.cropping === CropFit.CROP }, stdRadioTheme)),
            React.createElement(Radio, __assign({ label: t('editor.gallery.settings.cropping.fit'), value: 'fit', onChange: setCropRules, checked: state.cropping === CropFit.FIT }, stdRadioTheme))),
        state.galleryLayout === GalleryLayout.GRID && (React.createElement("div", { role: "group", "aria-labelledby": "grid-settings" },
            React.createElement("label", { className: s['label'], id: "grid-settings" }, t('editor.gallery.settings.gridSettings')),
            React.createElement("label", { className: s['number-setter'] },
                t('editor.gallery.settings.gridSettings.thumbWidth'),
                React.createElement("input", { className: s['range-slider'], style: { marginLeft: '13px' }, value: state.gridItemWidth, type: "range", step: "10", max: "400", min: "10", onChange: debounce(setGridItemWidth, 150, { leading: true }) }),
                React.createElement(Input, { className: s['range-input'], skin: { bar: s['range-input-bar'] }, type: 'number', value: state.gridItemWidth, onChange: setGridItemWidth, minValue: 10, maxValue: 400 })),
            React.createElement("label", { className: s['number-setter'] },
                t('editor.gallery.settings.gridSettings.thumbHeight'),
                React.createElement("input", { className: s['range-slider'], value: state.gridItemHeight, type: "range", step: "10", max: "400", min: "10", onChange: debounce(setGridItemHeight, 150, { leading: true }) }),
                React.createElement(Input, { className: s['range-input'], skin: { bar: s['range-input-bar'] }, type: 'number', value: state.gridItemHeight, onChange: setGridItemHeight, minValue: 10, maxValue: 400 })),
            React.createElement("label", { className: s['number-setter'] },
                t('editor.gallery.settings.spacing'),
                React.createElement("input", { className: s['range-slider'], value: state.spacing, type: "range", step: "1", max: "100", min: "0", onChange: debounce(setSpacing, 150, { leading: true }) }),
                React.createElement(Input, { className: s['range-input'], skin: { bar: s['range-input-bar'] }, type: 'number', value: state.spacing, onChange: setSpacing, minValue: 0, maxValue: 100 })))),
        state.galleryLayout === GalleryLayout.MASONRY && (React.createElement("div", { role: "group", "aria-labelledby": "masonry-settings" },
            React.createElement("label", { className: s['label'], id: "masonry-settings" }, t('editor.gallery.settings.masonrySettings')),
            React.createElement("label", { className: s['number-setter'] },
                t('editor.gallery.settings.masonrySettings.rowHeight'),
                React.createElement("input", { className: s['range-slider'], value: state.masonryRowHeight, type: "range", step: "10", max: "400", min: "10", onChange: debounce(setMasonryRowHeight, 150, { leading: true }) }),
                React.createElement(Input, { className: s['range-input'], skin: { bar: s['range-input-bar'] }, type: 'number', value: state.masonryRowHeight, onChange: setMasonryRowHeight, minValue: 10, maxValue: 400 })),
            React.createElement("label", { className: s['number-setter'] },
                t('editor.gallery.settings.spacing'),
                React.createElement("input", { className: s['range-slider'], value: state.spacing, type: "range", step: "1", max: "100", min: "0", onChange: debounce(setSpacing, 150, { leading: true }) }),
                React.createElement(Input, { className: s['range-input'], skin: { bar: s['range-input-bar'] }, type: 'number', value: state.spacing, onChange: setSpacing, minValue: 0, maxValue: 100 })))),
        React.createElement("div", { className: s['footer'] },
            React.createElement(Button, { onClick: function () { return helpers.closeModal(); } }, "Done"))));
};
GallerySettingsModal.displayName = 'GallerySettingsModal';
export default GallerySettingsModal;
//# sourceMappingURL=gallery-settings-modal.js.map